import {
  CarrierPickupPoint,
  DeliveryMethodType,
  PickupPoint,
} from '@/interfaces/eshop'
import { Collection } from '@/interfaces/Collection'
import { StorefrontClient } from '@/lib/eshop/storefrontApi'
import { geocodeAddress, type LatLng } from '@/lib/mapy'

export type MapPickupPoint = {
  id: string
  name: string
  street: string
  city: string
  zip: string
  latitude: number
  longitude: number
  distanceKm?: number
}

export type PickupPointLoadParams = {
  center?: LatLng
  limit?: number
  radiusKm?: number
}

export type PickupPointSource = {
  load: (params: PickupPointLoadParams) => Promise<MapPickupPoint[]>
}

const geocodeCache = new Map<string, LatLng | null>()

async function geocodeCached(
  key: string,
  address: { street: string; city: string; zip: string; country: string },
): Promise<LatLng | null> {
  if (geocodeCache.has(key)) {
    return geocodeCache.get(key) ?? null
  }

  try {
    const result = await geocodeAddress(address)
    geocodeCache.set(key, result)
    return result
  } catch {
    geocodeCache.set(key, null)
    return null
  }
}

function parseCoord(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

export function createCarrierPickupSource(
  client: StorefrontClient,
  deliveryType: DeliveryMethodType,
): PickupPointSource {
  return {
    async load({ center, limit = 50, radiusKm = 20 }) {
      const params = new URLSearchParams()
      params.set('limit', String(limit))

      if (center) {
        params.set('nearLat', String(center.latitude))
        params.set('nearLng', String(center.longitude))
        params.set('radiusKm', String(radiusKm))
      }

      const response = await client.get<Collection<CarrierPickupPoint>>(
        `/delivery-methods/${deliveryType}/pickup-points?${params}`,
      )

      return response.data
        .map((point): MapPickupPoint | null => {
          const latitude = parseCoord(point.latitude)
          const longitude = parseCoord(point.longitude)
          if (latitude === null || longitude === null) return null

          return {
            id: point.id,
            name: point.name,
            street: point.street ?? '',
            city: point.city ?? '',
            zip: point.zip ?? '',
            latitude,
            longitude,
            distanceKm: point.distanceKm,
          }
        })
        .filter((point): point is MapPickupPoint => point !== null)
    },
  }
}

export function createPersonalPickupSource(
  client: StorefrontClient,
): PickupPointSource {
  return {
    async load() {
      const response = await client.get<Collection<PickupPoint>>(
        '/delivery-methods/personalPickup/pickup-points',
      )

      const points: MapPickupPoint[] = []

      for (const point of response.data) {
        const coords = await geocodeCached(point.id, {
          street: point.street,
          city: point.city,
          zip: point.zip,
          country: point.country,
        })

        if (!coords) continue

        points.push({
          id: point.id,
          name: point.name,
          street: point.street,
          city: point.city,
          zip: point.zip,
          latitude: coords.latitude,
          longitude: coords.longitude,
        })
      }

      return points
    },
  }
}
